import * as React from 'react';
import { useState } from 'react';

import useRouter from 'js/lib/useRouter';
import user from 'js/lib/user';

import useIsMounted from 'bundles/common/hooks/useIsMounted';
import ErrorPage from 'bundles/coursera-ui/components/rich/ErrorPage';
import BypassLogin from 'bundles/enterprise-legacy-learner-home/components/BypassLogin';
import { getAgeVerification } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeUtils';
import EnterpriseHomeAccessErrorPage from 'bundles/program-home/components/enterprise-home/EnterpriseHomeAccessErrorPage';
import { isForcedSSOLoginFlowEnabled } from 'bundles/saml-sso/epicFeatureFlags';
import AgeVerificationPage from 'bundles/unified-common/components/enterprise/AgeVerificationPageNonConfigurable';
import ProgramPreJoinCheck from 'bundles/unified-common/components/enterprise/ProgramPreJoinCheckNonConfigurable';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import {
  useAcceptanceNoticeLogs,
  useProgram,
  useProgramMembership,
  useRequirePreJoinCheck,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import useJwtInviteCheck from 'bundles/unified-common/hooks/useJwtInviteCheck';
import useSsoCheck from 'bundles/unified-common/hooks/useSsoCheck';

const ATTEMPT_SSO_LOGIN = 'attemptSSOLogin';

type Props = {
  children: React.ReactNode;
};

const EnterpriseUserStateCheckWrapper = ({ children }: Props) => {
  const [enableAgeVerification, setEnableAgeVerification] = useState(true);
  const isMounted = useIsMounted();
  const router = useRouter();
  const enterpriseProductFeatures = useEnterpriseProductFeatures();
  const acceptanceNoticeLogs = useAcceptanceNoticeLogs() || [];
  const program = useProgram();
  const programMembership = useProgramMembership();
  const requirePreJoinCheck = useRequirePreJoinCheck();
  const thirdPartyOrganization = useThirdPartyOrganization();

  const isAuthenticatedUser = user.isAuthenticatedUser();
  const userId = user.get()?.id;

  const { isProgramMember, isProgramAdmin } = programMembership;
  const shouldAttemptSsoLogin = router.location.query[ATTEMPT_SSO_LOGIN] === 'true';
  const isInLoginMode = router.location.query.authMode === 'login';

  useJwtInviteCheck({
    router,
    programMembership: {
      isInvited: programMembership.isInvited,
      isEmailNotVerified: !!programMembership.isEmailNotVerified,
    },
    isProgramAdmin,
    isAuthenticatedUser,
  });
  useSsoCheck({
    router,
    userId,
    isInLoginMode,
    program,
    thirdPartyOrganization,
    isProgramAdmin,
    isProgramMember,
    isAuthenticatedUser,
  });

  if (shouldAttemptSsoLogin && !isAuthenticatedUser && thirdPartyOrganization?.id) {
    return <BypassLogin thirdPartyOrganizationId={thirdPartyOrganization.id} />;
  } else if (
    enterpriseProductFeatures?.enableInvitedOnlyProgram &&
    (!isAuthenticatedUser ||
      (!programMembership?.isMembershipLoading &&
        !programMembership?.isProgramAdmin &&
        !programMembership?.isProgramMember &&
        !programMembership?.isInvited))
  ) {
    return (
      <ErrorPage>
        <EnterpriseHomeAccessErrorPage
          isLoggedIn={isAuthenticatedUser}
          thirdPartyOrganizationId={thirdPartyOrganization?.id}
          programId={program?.id}
        />
      </ErrorPage>
    );
  } else if (
    program &&
    requirePreJoinCheck &&
    acceptanceNoticeLogs.length === 0 &&
    !program?.endedAt &&
    !isForcedSSOLoginFlowEnabled()
  ) {
    const programPreJoinCheckProps = {
      isAuthenticatedUser,
      programId: program.id,
      programSlug: program.slug,
      programName: program.name,
      program,
      router,
      thirdPartyOrganizationId: thirdPartyOrganization?.id,
      thirdPartyOrganization,
      userId,
      enableSkillsInSearchAndBrowse: enterpriseProductFeatures?.enableSkillsInSearchAndBrowse,
      reloadProgramMembership: () => undefined,
    };
    return <ProgramPreJoinCheck {...programPreJoinCheckProps} />;
  } else if (
    program &&
    !isProgramMember &&
    enterpriseProductFeatures?.programJoinAgeVerification &&
    isMounted() &&
    enableAgeVerification &&
    !getAgeVerification(program.id, isAuthenticatedUser)
  ) {
    const ageVerificationPageProps = {
      programId: program.id,
      program,
      isAuthenticatedUser,
      setEnableAgeVerification,
    };
    return <AgeVerificationPage {...ageVerificationPageProps} />;
  }

  return <React.Fragment>{children}</React.Fragment>;
};

export default EnterpriseUserStateCheckWrapper;
